import React from 'react'
import {
  Heading,
  Text,
  LoadingSpinner,
  Button,
} from '../../components/ui'
import { PermissionGuard } from '../../components/PermissionGuard'
import { usePermission } from '../../hooks/usePermissions'
import { modulesService } from '../../services/modules'
import { comercialCockpitService } from '../../services/comercialCockpit'
import { dashcorretorService } from '../../services/dashcorretor'
import { BrokerCombobox } from '../Qualidade/BrokerCombobox'
import type { TicketBroker } from '../../types/quality'
import type {
  ComercialCockpitBrokerDetail,
  ComercialCockpitCampaigns,
  ComercialCockpitData,
  CockpitTimelineItem,
} from '../../types/comercialCockpit'
import type { HelpdeskQuote } from '../../types/comercialCotacoes'
import type { ComercialActivity, OverallFeedback } from '../../types/comercialAtividades'
import { FEEDBACK_BADGE_CLASS, FEEDBACK_LABELS } from '../../types/comercialAtividades'
import { ClassificationWithTrend } from '../../components/ClassificationTrendIndicator'
import {
  buildClassificationColorMap,
  buildClassificationRankMap,
} from '../../utils/classificationTrend'
import {
  formatCurrencyBRL,
  formatDatePtBr,
  formatDateTimePtBr,
  formatIntegerPtBr,
  formatQuoteLabel,
  getClientDisplayName,
  normalizeStatusKey,
  quoteStatusBadgeClass,
  truncateWithTooltip,
} from '../../utils/cotacaoFormatters'
import CockpitCampaignsDetailTab from './CockpitCampaignsDetailTab'
import CockpitAiAnalysisTab from './CockpitAiAnalysisTab'
import {
  ChartBarIcon,
  ClipboardDocumentListIcon,
  DocumentTextIcon,
  FaceSmileIcon,
  TrophyIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ClockIcon,
  HandThumbUpIcon,
  HandThumbDownIcon,
  MinusCircleIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline'
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from 'recharts'

const MODULE_NAME = 'comercial_cockpit'
const TIMELINE_DAYS = 90
const CHART_COLORS = ['#22c55e', '#6b7280', '#e11d48']

type ActiveTab = 'overview' | 'campaigns' | 'quotes' | 'activities' | 'analysis'

function getErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message
  const maybe = error as { message?: string; code?: string }
  if (maybe?.message) return maybe.message
  if (maybe?.code) return `Erro (${maybe.code})`
  return 'Erro inesperado ao carregar o cockpit.'
}

function tabClass(activeTab: ActiveTab, tab: ActiveTab) {
  return `py-3 px-1 border-b-2 font-medium text-sm ${
    activeTab === tab
      ? 'border-primary-600 text-primary-600 dark:text-primary-400'
      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-200'
  }`
}

function isOpenQuoteStatus(status: string | null | undefined): boolean {
  const key = normalizeStatusKey(status)
  return key === 'aberta' || key === 'em analise'
}

function countActiveCampaigns(campaigns: ComercialCockpitCampaigns): number {
  let count = 0
  if (campaigns.minimetasQuantidade > 0) count += 1
  if (campaigns.campeaoMes.length > 0) count += 1
  if (campaigns.decoleVendas.some((e) => e.value > 0)) count += 1
  if (campaigns.decoleProducao.some((e) => e.value > 0)) count += 1
  if (campaigns.reiImediatosQuantidade > 0) count += 1
  if (campaigns.bateuLevouJun && (campaigns.bateuLevouJun.paid_premium > 0 || campaigns.bateuLevouJun.pending_premium > 0)) count += 1
  if (campaigns.bateuLevouOut && (campaigns.bateuLevouOut.paid_premium > 0 || campaigns.bateuLevouOut.pending_premium > 0)) count += 1
  if (campaigns.indicacoesApp && campaigns.indicacoesApp.quantidade_total > 0) count += 1
  if (campaigns.indicacoesFarma && campaigns.indicacoesFarma.quantidade_total > 0) count += 1
  return count
}

function getLastInteractionDate(quotes: HelpdeskQuote[], activities: ComercialActivity[]): string | null {
  const dates: number[] = []
  quotes.forEach((q) => {
    if (q.inclusion_at) dates.push(new Date(q.inclusion_at).getTime())
  })
  activities.forEach((a) => {
    if (a.activity_date) dates.push(new Date(a.activity_date).getTime())
  })
  if (dates.length === 0) return null
  return new Date(Math.max(...dates)).toISOString()
}

function getPredominantFeedback(activities: ComercialActivity[]): OverallFeedback | null {
  if (activities.length === 0) return null
  const counts: Record<OverallFeedback, number> = { positivo: 0, neutro: 0, negativo: 0 }
  activities.forEach((a) => {
    counts[a.overall_feedback] += 1
  })
  return (Object.entries(counts).sort((a, b) => b[1] - a[1])[0]?.[0] ?? null) as OverallFeedback | null
}

function buildTimeline(quotes: HelpdeskQuote[], activities: ComercialActivity[]): CockpitTimelineItem[] {
  const cutoff = Date.now() - TIMELINE_DAYS * 24 * 60 * 60 * 1000
  const items: CockpitTimelineItem[] = []

  activities.forEach((activity) => {
    const time = new Date(activity.activity_date).getTime()
    if (!Number.isNaN(time) && time >= cutoff) {
      items.push({ type: 'activity', date: activity.activity_date, activity })
    }
  })

  quotes.forEach((quote) => {
    if (!quote.inclusion_at) return
    const time = new Date(quote.inclusion_at).getTime()
    if (!Number.isNaN(time) && time >= cutoff) {
      items.push({ type: 'quote', date: quote.inclusion_at, quote })
    }
  })

  return items.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
}

function formatMonthLabel(yearMonth: string): string {
  const [year, month] = yearMonth.split('-')
  const date = new Date(Number(year), Number(month) - 1, 1)
  return date.toLocaleDateString('pt-BR', { month: 'short', year: '2-digit' })
}

function buildActivitiesByMonth(activities: ComercialActivity[]) {
  const now = new Date()
  const months: { key: string; name: string; count: number }[] = []
  for (let i = 5; i >= 0; i -= 1) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1)
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
    months.push({ key, name: formatMonthLabel(key), count: 0 })
  }
  const monthMap = new Map(months.map((m) => [m.key, m]))
  activities.forEach((a) => {
    const key = a.activity_date.slice(0, 7)
    const entry = monthMap.get(key)
    if (entry) entry.count += 1
  })
  return months
}

const FeedbackIcon: React.FC<{ feedback: OverallFeedback | null }> = ({ feedback }) => {
  if (feedback === 'positivo') return <HandThumbUpIcon className="h-5 w-5 text-green-600 dark:text-green-400" />
  if (feedback === 'negativo') return <HandThumbDownIcon className="h-5 w-5 text-red-600 dark:text-red-400" />
  if (feedback === 'neutro') return <MinusCircleIcon className="h-5 w-5 text-gray-500 dark:text-gray-400" />
  return <FaceSmileIcon className="h-5 w-5 text-gray-400" />
}

const KpiCard: React.FC<{
  title: string
  value: string
  subtitle?: string
  icon: React.ReactNode
}> = ({ title, value, subtitle, icon }) => (
  <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 flex items-start gap-3">
    <div className="p-2 rounded-lg bg-primary-50 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400 shrink-0">
      {icon}
    </div>
    <div className="min-w-0">
      <Text size="xs" color="muted" className="uppercase tracking-wide">
        {title}
      </Text>
      <p className="text-2xl font-semibold text-gray-900 dark:text-white mt-0.5">{value}</p>
      {subtitle && (
        <Text size="xs" color="muted" className="mt-1">
          {subtitle}
        </Text>
      )}
    </div>
  </div>
)

const BrokerProfileCard: React.FC<{
  broker: ComercialCockpitBrokerDetail
  colorMap: Map<string, string | null>
  rankMap: Map<string, number>
}> = ({ broker, colorMap, rankMap }) => {
  const [contactsOpen, setContactsOpen] = React.useState(false)
  const location = [broker.state, broker.city].filter(Boolean).join(' / ') || '—'

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-5">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <Heading as="h2" size="xl" className="text-gray-900 dark:text-white">
            {broker.name}
          </Heading>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2">
            <Text size="sm" color="muted">
              Código SCGP: <span className="font-medium text-gray-800 dark:text-gray-200">{broker.id_scgp ?? '—'}</span>
            </Text>
            <Text size="sm" color="muted">
              Categoria:{' '}
              <ClassificationWithTrend
                classification={broker.classification}
                classificationPrevious={broker.classification_previous}
                rankMap={rankMap}
                colorMap={colorMap}
              />
            </Text>
            <Text size="sm" color="muted">
              Local: <span className="font-medium text-gray-800 dark:text-gray-200">{location}</span>
            </Text>
            <Text size="sm" color="muted">
              Comercial:{' '}
              <span className="font-medium text-gray-800 dark:text-gray-200">
                {broker.comercial_name ?? 'Não atribuído'}
              </span>
            </Text>
            {broker.classification?.toUpperCase() === 'START' && (
              <span
                className={`text-xs px-2 py-0.5 rounded-full border ${
                  broker.has_production
                    ? 'bg-green-100 text-green-800 border-green-200 dark:bg-green-900/40 dark:text-green-200'
                    : 'bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-900/40 dark:text-amber-200'
                }`}
              >
                {broker.has_production ? 'Com produção' : 'Sem produção'}
              </span>
            )}
          </div>
        </div>
        {broker.contacts.length > 0 && (
          <Button
            type="button"
            variant="outline"
            onClick={() => setContactsOpen((v) => !v)}
            className="flex items-center gap-1"
          >
            Contatos ({broker.contacts.length})
            {contactsOpen ? <ChevronUpIcon className="h-4 w-4" /> : <ChevronDownIcon className="h-4 w-4" />}
          </Button>
        )}
      </div>

      {contactsOpen && broker.contacts.length > 0 && (
        <div className="mt-4 overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 dark:border-gray-700 text-left text-gray-500 dark:text-gray-400">
                <th className="py-2 pr-4 font-medium">Nome</th>
                <th className="py-2 pr-4 font-medium">Cargo</th>
                <th className="py-2 pr-4 font-medium">Telefone</th>
                <th className="py-2 font-medium">E-mail</th>
              </tr>
            </thead>
            <tbody>
              {broker.contacts.map((contact) => (
                <tr key={contact.id} className="border-b border-gray-100 dark:border-gray-700/60">
                  <td className="py-2 pr-4 text-gray-900 dark:text-gray-100">
                    {contact.name}
                    {contact.is_partner && (
                      <span className="ml-1 text-xs text-primary-600 dark:text-primary-400">(Sócio)</span>
                    )}
                  </td>
                  <td className="py-2 pr-4 text-gray-700 dark:text-gray-300">{contact.role ?? '—'}</td>
                  <td className="py-2 pr-4 text-gray-700 dark:text-gray-300">{contact.phone ?? '—'}</td>
                  <td className="py-2 text-gray-700 dark:text-gray-300">{contact.email ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

const ComercialCockpit: React.FC = () => {
  const { hasPermission: canViewValues } = usePermission(MODULE_NAME, 'view_values')
  const [moduleInfo, setModuleInfo] = React.useState<{
    display_name: string
    description?: string | null
  } | null>(null)
  const [selectedBrokerId, setSelectedBrokerId] = React.useState<string | null>(null)
  const [selectedBroker, setSelectedBroker] = React.useState<TicketBroker | null>(null)
  const [cockpitData, setCockpitData] = React.useState<ComercialCockpitData | null>(null)
  const [loading, setLoading] = React.useState(false)
  const [pageError, setPageError] = React.useState<string | null>(null)
  const [activeTab, setActiveTab] = React.useState<ActiveTab>('overview')
  const [colorMap, setColorMap] = React.useState<Map<string, string | null>>(new Map())
  const [rankMap, setRankMap] = React.useState<Map<string, number>>(new Map())

  React.useEffect(() => {
    const loadModule = async () => {
      const result = await modulesService.getByName(MODULE_NAME)
      if (result.success && result.data) {
        setModuleInfo({
          display_name: result.data.display_name ?? 'Cockpit do Corretor',
          description: result.data.description ?? null,
        })
      }
    }
    void loadModule()
  }, [])

  React.useEffect(() => {
    const loadClassifications = async () => {
      try {
        const refs = await dashcorretorService.listClassificationReferences()
        setColorMap(buildClassificationColorMap(refs))
        setRankMap(buildClassificationRankMap(refs))
      } catch {
        setColorMap(new Map())
        setRankMap(new Map())
      }
    }
    void loadClassifications()
  }, [])

  const loadCockpit = React.useCallback(async (brokerId: string) => {
    setLoading(true)
    setPageError(null)
    try {
      const data = await comercialCockpitService.loadBrokerCockpit(brokerId)
      setCockpitData(data)
    } catch (error) {
      setCockpitData(null)
      setPageError(getErrorMessage(error))
    } finally {
      setLoading(false)
    }
  }, [])

  const handleBrokerChange = (id: string | null, broker: TicketBroker | null) => {
    setSelectedBrokerId(id)
    setSelectedBroker(broker)
    setCockpitData(null)
    setPageError(null)
    setActiveTab('overview')
    if (id) {
      void loadCockpit(id)
    }
  }

  const quotes = cockpitData?.quotes ?? []
  const activities = cockpitData?.activities ?? []
  const campaigns = cockpitData?.campaigns
  const campaignContext = cockpitData?.campaignContext
  const broker = cockpitData?.broker

  const openQuotesCount = React.useMemo(
    () => quotes.filter((q) => isOpenQuoteStatus(q.status)).length,
    [quotes],
  )

  const activitiesLast30Days = React.useMemo(() => {
    const cutoff = Date.now() - 30 * 24 * 60 * 60 * 1000
    return activities.filter((a) => new Date(a.activity_date).getTime() >= cutoff).length
  }, [activities])

  const activeCampaignsCount = campaigns ? countActiveCampaigns(campaigns) : 0
  const lastInteraction = getLastInteractionDate(quotes, activities)
  const predominantFeedback = getPredominantFeedback(activities)
  const timeline = React.useMemo(() => buildTimeline(quotes, activities), [quotes, activities])
  const activitiesByMonth = React.useMemo(() => buildActivitiesByMonth(activities), [activities])

  const feedbackChartData = React.useMemo(
    () => [
      { name: FEEDBACK_LABELS.positivo, value: activities.filter((a) => a.overall_feedback === 'positivo').length },
      { name: FEEDBACK_LABELS.neutro, value: activities.filter((a) => a.overall_feedback === 'neutro').length },
      { name: FEEDBACK_LABELS.negativo, value: activities.filter((a) => a.overall_feedback === 'negativo').length },
    ],
    [activities],
  )

  return (
    <div className="flex-1 flex flex-col overflow-x-hidden overflow-y-auto">
        <PermissionGuard moduleName={MODULE_NAME} permissionType="show">
          <div className="h-full flex flex-col">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <Heading as="h1" size="3xl" className="text-gray-900 dark:text-white">
                {moduleInfo?.display_name ?? 'Cockpit do Corretor'}
              </Heading>
              {moduleInfo?.description && (
                <Text size="sm" color="muted" className="mt-1">
                  {moduleInfo.description}
                </Text>
              )}
            </div>

            <div className="p-6 space-y-6">
              <div className="max-w-xl">
                <Text size="sm" className="mb-2 font-medium text-gray-700 dark:text-gray-300">
                  Localizar corretor
                </Text>
                <BrokerCombobox
                  value={selectedBrokerId}
                  initialBroker={
                    selectedBroker
                      ? { name: selectedBroker.name, id_scgp: selectedBroker.id_scgp }
                      : null
                  }
                  onChange={handleBrokerChange}
                  placeholder="Digite o código SCGP ou nome do corretor (mín. 4 caracteres)"
                />
              </div>

              {!selectedBrokerId && !loading && (
                <div className="bg-white dark:bg-gray-800 rounded-lg border border-dashed border-gray-300 dark:border-gray-600 p-10 text-center">
                  <ChartBarIcon className="h-12 w-12 mx-auto text-gray-400 dark:text-gray-500 mb-3" />
                  <Text color="muted">
                    Busque um corretor para visualizar o painel consolidado de campanhas, cotações e atividades.
                  </Text>
                </div>
              )}

              {loading && (
                <div className="flex items-center justify-center py-16">
                  <LoadingSpinner />
                </div>
              )}

              {pageError && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg dark:bg-red-900/20 dark:border-red-800 dark:text-red-300">
                  <Text size="sm">{pageError}</Text>
                </div>
              )}

              {!loading && broker && cockpitData && (
                <>
                  <BrokerProfileCard broker={broker} colorMap={colorMap} rankMap={rankMap} />

                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
                    <KpiCard
                      title="Cotações"
                      value={formatIntegerPtBr(quotes.length)}
                      subtitle={`${openQuotesCount} aberta(s) / em análise`}
                      icon={<DocumentTextIcon className="h-5 w-5" />}
                    />
                    <KpiCard
                      title="Atividades"
                      value={formatIntegerPtBr(activities.length)}
                      subtitle={`${activitiesLast30Days} nos últimos 30 dias`}
                      icon={<ClipboardDocumentListIcon className="h-5 w-5" />}
                    />
                    <KpiCard
                      title="Campanhas ativas"
                      value={formatIntegerPtBr(activeCampaignsCount)}
                      subtitle="Com participação em 2026"
                      icon={<TrophyIcon className="h-5 w-5" />}
                    />
                    <KpiCard
                      title="Última interação"
                      value={lastInteraction ? formatDatePtBr(lastInteraction) : '—'}
                      subtitle={lastInteraction ? formatDateTimePtBr(lastInteraction).split(' ')[1] ?? '' : 'Sem registros'}
                      icon={<ClockIcon className="h-5 w-5" />}
                    />
                    <KpiCard
                      title="Feedback predominante"
                      value={predominantFeedback ? FEEDBACK_LABELS[predominantFeedback] : '—'}
                      subtitle={
                        predominantFeedback
                          ? `${activities.filter((a) => a.overall_feedback === predominantFeedback).length} de ${activities.length}`
                          : 'Sem atividades'
                      }
                      icon={<FeedbackIcon feedback={predominantFeedback} />}
                    />
                  </div>

                  <div className="border-b border-gray-200 dark:border-gray-700">
                    <nav className="-mb-px flex flex-wrap gap-x-8">
                      <button type="button" onClick={() => setActiveTab('overview')} className={tabClass(activeTab, 'overview')}>
                        Visão Geral
                      </button>
                      <button type="button" onClick={() => setActiveTab('campaigns')} className={tabClass(activeTab, 'campaigns')}>
                        Campanhas 2026
                      </button>
                      <button type="button" onClick={() => setActiveTab('quotes')} className={tabClass(activeTab, 'quotes')}>
                        Cotações
                      </button>
                      <button type="button" onClick={() => setActiveTab('activities')} className={tabClass(activeTab, 'activities')}>
                        Atividades
                      </button>
                      <button type="button" onClick={() => setActiveTab('analysis')} className={tabClass(activeTab, 'analysis')}>
                        <span className="inline-flex items-center gap-1">
                          <SparklesIcon className="h-4 w-4" />
                          Análise IA
                        </span>
                      </button>
                    </nav>
                  </div>

                  {activeTab === 'overview' && (
                    <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
                        <Heading as="h3" size="xl" className="text-gray-900 dark:text-white mb-4">
                          Timeline - últimos {TIMELINE_DAYS} dias
                        </Heading>
                        {timeline.length === 0 ? (
                          <Text size="sm" color="muted">Nenhum evento recente.</Text>
                        ) : (
                          <ul className="space-y-3 max-h-96 overflow-y-auto pr-1">
                            {timeline.map((item) => (
                              <li
                                key={`${item.type}-${item.type === 'activity' ? item.activity.id : item.quote.id}`}
                                className="flex gap-3 border-l-2 border-primary-200 dark:border-primary-800 pl-3"
                              >
                                <div className="shrink-0 mt-0.5">
                                  {item.type === 'activity' ? (
                                    <ClipboardDocumentListIcon className="h-4 w-4 text-primary-600 dark:text-primary-400" />
                                  ) : (
                                    <DocumentTextIcon className="h-4 w-4 text-violet-600 dark:text-violet-400" />
                                  )}
                                </div>
                                <div className="min-w-0">
                                  <Text size="xs" color="muted">{formatDatePtBr(item.date)}</Text>
                                  {item.type === 'activity' ? (
                                    <>
                                      <Text size="sm" className="font-medium text-gray-900 dark:text-white">
                                        {item.activity.activity_type?.name ?? 'Atividade'}
                                      </Text>
                                      <Text size="xs" color="muted">
                                        {item.activity.visit_objective?.name ?? '—'} · {FEEDBACK_LABELS[item.activity.overall_feedback]}
                                      </Text>
                                    </>
                                  ) : (
                                    <>
                                      <Text size="sm" className="font-medium text-gray-900 dark:text-white">
                                        Cotação {formatQuoteLabel(item.quote.quote_number, item.quote.version)}
                                      </Text>
                                      <Text size="xs" color="muted">
                                        {getClientDisplayName(item.quote)} · {item.quote.status ?? '—'}
                                      </Text>
                                    </>
                                  )}
                                </div>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>

                      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
                        <Heading as="h3" size="xl" className="text-gray-900 dark:text-white mb-4">
                          Atividades por mês
                        </Heading>
                        <div className="h-64">
                          <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={activitiesByMonth}>
                              <CartesianGrid strokeDasharray="3 3" className="stroke-gray-200 dark:stroke-gray-700" />
                              <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                              <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                              <Tooltip />
                              <Bar dataKey="count" name="Atividades" fill="#008B95" radius={[4, 4, 0, 0]} />
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                      </div>

                      <div className="xl:col-span-2 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
                        <Heading as="h3" size="xl" className="text-gray-900 dark:text-white mb-4">
                          Resumo de campanhas 2026
                        </Heading>
                        <div className="flex flex-wrap gap-2">
                          {[
                            { label: 'Minimetas', active: (campaigns?.minimetasQuantidade ?? 0) > 0 },
                            { label: 'Campeão do Mês', active: (campaigns?.campeaoMes.length ?? 0) > 0 },
                            { label: 'Decole Vendas', active: campaigns?.decoleVendas.some((e) => e.value > 0) ?? false },
                            { label: 'Decole Produção', active: campaigns?.decoleProducao.some((e) => e.value > 0) ?? false },
                            { label: 'Rei dos Imediatos', active: (campaigns?.reiImediatosQuantidade ?? 0) > 0 },
                            { label: 'Bateu Levou (Jun)', active: !!campaigns?.bateuLevouJun },
                            { label: 'Bateu Levou (Out)', active: !!campaigns?.bateuLevouOut },
                            { label: 'PASI App', active: (campaigns?.indicacoesApp?.quantidade_total ?? 0) > 0 },
                            { label: 'PASI Farma', active: (campaigns?.indicacoesFarma?.quantidade_total ?? 0) > 0 },
                          ].map((item) => (
                            <span
                              key={item.label}
                              className={`text-xs px-2.5 py-1 rounded-full border ${
                                item.active
                                  ? 'bg-primary-50 text-primary-800 border-primary-200 dark:bg-primary-900/30 dark:text-primary-200 dark:border-primary-800'
                                  : 'bg-gray-100 text-gray-500 border-gray-200 dark:bg-gray-700 dark:text-gray-400 dark:border-gray-600'
                              }`}
                            >
                              {item.label}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {activeTab === 'campaigns' && campaigns && campaignContext && broker && (
                    <CockpitCampaignsDetailTab
                      broker={broker}
                      campaigns={campaigns}
                      campaignContext={campaignContext}
                      colorMap={colorMap}
                      canViewValues={canViewValues}
                    />
                  )}

                  {activeTab === 'quotes' && (
                    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-x-auto">
                      <table className="min-w-full text-sm">
                        <thead>
                          <tr className="bg-gray-50 dark:bg-gray-900/50 border-b border-gray-200 dark:border-gray-700 text-left text-gray-500 dark:text-gray-400">
                            <th className="px-4 py-3 font-medium">Cotação</th>
                            <th className="px-4 py-3 font-medium">Empresa</th>
                            <th className="px-4 py-3 font-medium">Status</th>
                            <th className="px-4 py-3 font-medium">Vidas</th>
                            <th className="px-4 py-3 font-medium">Prêmio/vida</th>
                            <th className="px-4 py-3 font-medium">Inclusão</th>
                            <th className="px-4 py-3 font-medium">Responsável</th>
                          </tr>
                        </thead>
                        <tbody>
                          {quotes.length === 0 ? (
                            <tr>
                              <td colSpan={7} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
                                Nenhuma cotação encontrada para este corretor.
                              </td>
                            </tr>
                          ) : (
                            quotes.map((quote) => {
                              const client = truncateWithTooltip(getClientDisplayName(quote), 40)
                              return (
                                <tr key={quote.id} className="border-b border-gray-100 dark:border-gray-700/60">
                                  <td className="px-4 py-3 text-gray-900 dark:text-gray-100">
                                    {formatQuoteLabel(quote.quote_number, quote.version)}
                                  </td>
                                  <td className="px-4 py-3 text-gray-700 dark:text-gray-300" title={client.title}>
                                    {client.display}
                                  </td>
                                  <td className="px-4 py-3">
                                    <span
                                      className={`inline-flex text-xs px-2 py-0.5 rounded-full border ${quoteStatusBadgeClass(quote.status)}`}
                                    >
                                      {quote.status ?? '—'}
                                    </span>
                                  </td>
                                  <td className="px-4 py-3 text-gray-700 dark:text-gray-300">
                                    {formatIntegerPtBr(quote.insured_count)}
                                  </td>
                                  <td className="px-4 py-3 text-gray-700 dark:text-gray-300">
                                    {canViewValues ? formatCurrencyBRL(quote.amount_per_life) : '—'}
                                  </td>
                                  <td className="px-4 py-3 text-gray-700 dark:text-gray-300">
                                    {formatDatePtBr(quote.inclusion_at)}
                                  </td>
                                  <td className="px-4 py-3 text-gray-700 dark:text-gray-300">
                                    {quote.responsible ?? '—'}
                                  </td>
                                </tr>
                              )
                            })
                          )}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {activeTab === 'activities' && (
                    <div className="space-y-6">
                      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
                        <Heading as="h3" size="xl" className="text-gray-900 dark:text-white mb-4">
                          Distribuição de feedback
                        </Heading>
                        <div className="h-56">
                          <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                              <Pie
                                data={feedbackChartData}
                                dataKey="value"
                                nameKey="name"
                                cx="50%"
                                cy="50%"
                                outerRadius={80}
                                label={({ name, value }) => (value > 0 ? `${name}: ${value}` : '')}
                              >
                                {feedbackChartData.map((_, index) => (
                                  <Cell key={index} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                                ))}
                              </Pie>
                              <Tooltip />
                            </PieChart>
                          </ResponsiveContainer>
                        </div>
                      </div>

                      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-x-auto">
                        <table className="min-w-full text-sm">
                          <thead>
                            <tr className="bg-gray-50 dark:bg-gray-900/50 border-b border-gray-200 dark:border-gray-700 text-left text-gray-500 dark:text-gray-400">
                              <th className="px-4 py-3 font-medium">Data</th>
                              <th className="px-4 py-3 font-medium">Tipo</th>
                              <th className="px-4 py-3 font-medium">Objetivo</th>
                              <th className="px-4 py-3 font-medium">Feedback</th>
                              <th className="px-4 py-3 font-medium">Percepções</th>
                              <th className="px-4 py-3 font-medium">Responsável</th>
                            </tr>
                          </thead>
                          <tbody>
                            {activities.length === 0 ? (
                              <tr>
                                <td colSpan={6} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
                                  Nenhuma atividade registrada para este corretor.
                                </td>
                              </tr>
                            ) : (
                              activities.map((activity) => {
                                const perceptions = truncateWithTooltip(activity.general_perceptions, 50)
                                return (
                                  <tr key={activity.id} className="border-b border-gray-100 dark:border-gray-700/60">
                                    <td className="px-4 py-3 text-gray-900 dark:text-gray-100">
                                      {formatDatePtBr(activity.activity_date)}
                                    </td>
                                    <td className="px-4 py-3 text-gray-700 dark:text-gray-300">
                                      {activity.activity_type?.name ?? '—'}
                                    </td>
                                    <td className="px-4 py-3 text-gray-700 dark:text-gray-300">
                                      {activity.visit_objective?.name ?? '—'}
                                    </td>
                                    <td className="px-4 py-3">
                                      <span
                                        className={`inline-flex text-xs px-2 py-0.5 rounded-full ${FEEDBACK_BADGE_CLASS[activity.overall_feedback]}`}
                                      >
                                        {FEEDBACK_LABELS[activity.overall_feedback]}
                                      </span>
                                    </td>
                                    <td className="px-4 py-3 text-gray-700 dark:text-gray-300" title={perceptions.title}>
                                      {perceptions.display}
                                    </td>
                                    <td className="px-4 py-3 text-gray-700 dark:text-gray-300">
                                      {activity.commercial_responsible_name}
                                    </td>
                                  </tr>
                                )
                              })
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {activeTab === 'analysis' && broker && (
                    <CockpitAiAnalysisTab
                      brokerId={broker.id}
                      idScgp={broker.id_scgp}
                      brokerName={broker.name}
                    />
                  )}
                </>
              )}
            </div>
          </div>
        </PermissionGuard>
    </div>
  )
}

export default ComercialCockpit
